import {Annotation, IconMap, useAnnotationCollapseState} from '@github-ui/diff-lines/annotation'
import {ZeroState} from './ZeroState'
import {AlertIcon, ChevronDownIcon, ChevronRightIcon, FileSymlinkFileIcon, XIcon} from '@primer/octicons-react'
import {Banner, Heading, IconButton, Link} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {memo, useEffect, useCallback, useRef, useState, useMemo} from 'react'
import {announce} from '@github-ui/aria-live'
import {debounce} from '@github/mini-throttle'
import {AlertsFilter, getFilteredAlerts} from './AlertsFilter'
import styles from './AlertsSidePanel.module.css'
import {clsx} from 'clsx'
import {Tooltip} from '@primer/react/next'
import type {PageLimits} from '../../page-data/payloads/files'
import {
  useAnnotations,
  useAnnotationsPageInfo,
  useNavigateToAnnotation,
  usePullRequestBasePath,
} from '../../stores/layout-selectors'
import type {DiffAnnotation, DiffAnnotationLevels} from '@github-ui/diff-lines/conversation-types'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {noop} from '@github-ui/noop'
import {useNavigate} from '@github-ui/use-navigate'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {getPanelHeading} from './panel-registry'
import {useInfiniteAnnotationsPageData} from '../../page-data/loaders/use-annotations'
import {useHeaderPageData} from '../../page-data/loaders/use-header-page-data'
import {ThreadPreviewSkeleton} from './ThreadPreviewSkeleton'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useActiveRoute} from '../../hooks/use-active-route'

export interface AlertsSidePanelProps {
  pageLimits?: PageLimits
}

function AnnotationHeader({
  databaseId,
  lineNumber,
  path,
  toggleCollapsed,
  isCollapsed,
  onNavigateToAnnotation,
  annotationLevel,
}: {
  databaseId: number
  lineNumber: number
  path: string
  toggleCollapsed: () => void
  isCollapsed: boolean
  onNavigateToAnnotation: () => void
  annotationLevel: DiffAnnotationLevels
}) {
  return (
    <div className="d-flex flex-row flex-items-center py-1 px-2 bgColor-inset rounded-top-2 border-bottom">
      <h2 className="d-flex flex-items-center flex-1 min-width-0 ml-1 mr-2">
        <IconButton
          aria-label={isCollapsed ? 'Expand annotation' : 'Collapse annotation'}
          icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          variant="invisible"
          className="mr-1"
          onClick={toggleCollapsed}
        />

        {IconMap[annotationLevel]}
        <Tooltip direction="n" text={path} type="label">
          <Link
            className={clsx('overflow-hidden text-mono text-semibold f6 no-wrap', styles.annotationsHeaderFileName)}
            href={`#annotation_${databaseId}`}
            onClick={onNavigateToAnnotation}
            muted
          >
            {/* Ensures path is displayed in left-to-right-order despite container direction being right-to-left */}
            &lrm;{path}
          </Link>
        </Tooltip>
        <span className="f6 fgColor-muted text-normal ml-2 no-wrap">Line {lineNumber}</span>
      </h2>
      <IconButton
        as="a"
        aria-label="Jump to the alert in the diff"
        tooltipDirection="se"
        icon={FileSymlinkFileIcon}
        variant="invisible"
        href={`#annotation_${databaseId}`}
        onClick={onNavigateToAnnotation}
      />
    </div>
  )
}

/**
 * Renders the header content for the Alerts Side Panel, containing
 * a text filter for searching annotations.
 */
function AlertsPanelHeaderContent({
  filteredText,
  onFilteredTextChange,
}: {
  filteredText: string
  onFilteredTextChange: (text: string) => void
}) {
  return (
    <AlertsFilter className="mt-2 width-full" filteredText={filteredText} onFilteredTextChange={onFilteredTextChange} />
  )
}

/**
 * Renders the body content of the Alerts Side Panel.
 *
 * Displays annotation summaries filtered by the current text filter.
 * When the annotation limit is exceeded, a warning banner is shown.
 */
function AlertsPanelBody({
  annotationSummaries,
  hasAlerts,
  isLoadingMore,
  pageLimits,
  announceRef,
}: {
  annotationSummaries: React.ReactNode[]
  hasAlerts: boolean
  isLoadingMore: boolean
  pageLimits?: PageLimits
  announceRef: React.RefObject<HTMLDivElement | null>
}) {
  return (
    <>
      {pageLimits && pageLimits.annotationsPaginatedLimitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the first ${pageLimits.annotationsPaginatedLimit} alerts are currently being shown.`}
          className="tmp-mb-3"
        />
      )}
      {annotationSummaries.length > 0 ? (
        <div className="d-flex flex-column position-relative width-full gap-3">
          {annotationSummaries}
          {isLoadingMore && <ThreadPreviewSkeleton count={1} />}
        </div>
      ) : isLoadingMore ? (
        <ThreadPreviewSkeleton />
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            heading={hasAlerts ? 'No alerts match the current filter' : 'No alerts on changes yet'}
            icon={AlertIcon}
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

/**
 *
 * Renders a side panel displaying previews of the pull request's annotations
 *
 * Self-contained: sources its own data from hooks/context.
 * Rendered by DockedSidePanelContent when the alerts panel is active.
 * In non-compact mode, renders inline Panel content. In compact mode,
 * falls back to a Dialog sidesheet.
 */
export const AlertsSidePanel = memo(function AlertsSidePanelComponent({pageLimits}: AlertsSidePanelProps) {
  const isOpen = useIsDockedPanelOpen(DockedPanel.Alerts)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const annotations = useAnnotations()
  const pathName = usePullRequestBasePath()
  const {
    data: {
      pullRequest: {headSha: endCommitOid},
    },
  } = useHeaderPageData()

  const [filteredText, setFilteredText] = useState('')
  const navigateToSelectedAnnotation = useNavigateToAnnotation()
  const navigate = useNavigate()
  const annotationsPageInfo = useAnnotationsPageInfo()

  const activeRoute = useActiveRoute()
  const isFilesChanged = activeRoute === 'files_changed'
  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')
  // Determine if we're on a route that requires lazy loading annotations (non-files page with flag).
  const isLazyLoadRoute = !isFilesChanged && updatedPullRequestHeader

  // Fetch paginated annotations when feature flag is enabled.
  // When lazy loading (non-files page), fetch from scratch with hasMorePages: true.
  // Otherwise, pass the initial annotations from the store as the first page to avoid re-fetching.
  const {
    hasNextPage,
    isFetchingNextPage: isFetchingAnnotations,
    fetchNextPage,
  } = useInfiniteAnnotationsPageData({
    pathName,
    endCommitOid,
    initialAnnotations: isLazyLoadRoute ? [] : annotations,
    cursor: isLazyLoadRoute ? undefined : annotationsPageInfo?.cursor,
    hasMorePages: isLazyLoadRoute ? true : (annotationsPageInfo?.hasNextPage ?? false),
  })

  // Track if we've initiated a fetch to prevent duplicate requests
  const fetchingRef = useRef(false)

  // Automatically fetch next page when panel is open and not already fetching
  useEffect(() => {
    const fetchAllPages = async () => {
      if (isOpen && hasNextPage && !isFetchingAnnotations && !fetchingRef.current) {
        fetchingRef.current = true
        await fetchNextPage()
        fetchingRef.current = false
      }
    }
    void fetchAllPages()
  }, [isOpen, hasNextPage, isFetchingAnnotations, fetchNextPage])

  // Show loading state when actively fetching OR when there's a next page about to be fetched
  const isLoadingMore = isFetchingAnnotations || hasNextPage

  const filteredAnnotationIds = getFilteredAlerts(annotations, filteredText)
  const hasAlerts = annotations.length > 0

  // In docked panel mode, don't close the panel when navigating to an annotation.
  const effectiveOnClose = isOpen && !isCompact ? noop : closePanel

  const handleNavigateToAnnotation = useCallback(
    (annotationId: number) => {
      if (navigateToSelectedAnnotation) {
        navigateToSelectedAnnotation(annotationId)
      } else {
        navigate(`${pathName}/changes#annotation_${annotationId}`)
      }
      effectiveOnClose()
    },
    [effectiveOnClose, navigateToSelectedAnnotation, pathName, navigate],
  )

  const annotationSummaries = annotations
    .map(annotation => {
      if (!annotation || !filteredAnnotationIds.has(annotation.id)) return null
      return (
        <SidePanelAlert
          annotation={annotation}
          key={annotation.id}
          handleNavigateToAnnotation={handleNavigateToAnnotation}
        />
      )
    })
    .filter(Boolean)

  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  useEffect(() => {
    if (!isOpen) return
    // Only announce when we've finished loading all annotations
    if (isLoadingMore) return

    const announceText =
      annotationSummaries.length > 0
        ? `${annotationSummaries.length} ${annotationSummaries.length === 1 ? 'alert' : 'alerts'}`
        : `No alerts found`
    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, isLoadingMore, debouncedAnnounce, annotationSummaries])

  if (!isOpen) return null

  if (!isCompact) {
    return (
      <Panel panelName={DockedPanel.Alerts}>
        <Panel.Header>
          <AlertsPanelHeaderContent filteredText={filteredText} onFilteredTextChange={setFilteredText} />
        </Panel.Header>
        <Panel.Body>
          <AlertsPanelBody
            annotationSummaries={annotationSummaries}
            hasAlerts={hasAlerts}
            isLoadingMore={isLoadingMore}
            pageLimits={pageLimits}
            announceRef={announceRef}
          />
        </Panel.Body>
      </Panel>
    )
  }

  if (!updatedPullRequestHeader) {
    return (
      <Dialog
        initialFocusRef={closeButtonRef}
        onClose={closePanel}
        position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
        returnFocusRef={returnFocusRef}
        renderHeader={({dialogLabelId}) => (
          <Dialog.Header>
            <div className="d-flex flex-row flex-items-center flex-justify-between width-full pl-2">
              <Heading as="h1" className="f4 text-bold" id={dialogLabelId}>
                {getPanelHeading(DockedPanel.Alerts)}
              </Heading>
              <IconButton
                ref={closeButtonRef}
                aria-label="Close alerts panel"
                icon={XIcon}
                variant="invisible"
                onClick={closePanel}
              />
            </div>
            <AlertsPanelHeaderContent filteredText={filteredText} onFilteredTextChange={setFilteredText} />
          </Dialog.Header>
        )}
      >
        <AlertsPanelBody
          annotationSummaries={annotationSummaries}
          hasAlerts={hasAlerts}
          isLoadingMore={isLoadingMore}
          pageLimits={pageLimits}
          announceRef={announceRef}
        />
      </Dialog>
    )
  }

  return (
    <>
      <CompactPanel.Header>
        <AlertsPanelHeaderContent filteredText={filteredText} onFilteredTextChange={setFilteredText} />
      </CompactPanel.Header>
      <CompactPanel.Content>
        <AlertsPanelBody
          annotationSummaries={annotationSummaries}
          hasAlerts={hasAlerts}
          isLoadingMore={isLoadingMore}
          pageLimits={pageLimits}
          announceRef={announceRef}
        />
      </CompactPanel.Content>
    </>
  )
})

function SidePanelAlert({
  annotation,
  handleNavigateToAnnotation,
}: {
  annotation: DiffAnnotation
  handleNavigateToAnnotation: (databaseId: number) => void
}): React.ReactElement {
  const {isCollapsed, handleToggleCollapsed} = useAnnotationCollapseState(annotation)

  return (
    <div key={annotation.id} className="border rounded-2 bgColor-default overflow-hidden">
      <AnnotationHeader
        databaseId={annotation.databaseId}
        lineNumber={annotation.endLine}
        annotationLevel={annotation.annotationLevel}
        path={annotation.path}
        toggleCollapsed={handleToggleCollapsed}
        isCollapsed={isCollapsed}
        onNavigateToAnnotation={() => handleNavigateToAnnotation(annotation.databaseId)}
      />
      {!isCollapsed && <Annotation annotation={annotation} inSidePanel />}
    </div>
  )
}
